/**
 * Users slice (PX-W6.2) — the org's people directory, invites, one user's
 * detail + activity, and the project members browser's rows.
 *
 * House rules (Redux canon): no reducer performs I/O — everything network
 * lives in `users.http.ts` behind `createAsyncThunk`; list reads are
 * fetchCache-guarded (`shouldFetch`), keyed on the active org id (org-keyed
 * thunks RETURN the org they loaded for, because the transport injects it
 * silently and the fulfilled reducer otherwise could not record it);
 * mutations MERGE the response row by id and invalidate the list caches they
 * stale — never a bare replace (INV-7).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as usersHttp from './users.http'
import type {
  ApiActivityEntry,
  ApiInvite,
  ApiProjectSummary,
  ApiUser,
  InviteInput,
  MemberRow,
  OrgRole,
  ProjectInviteInput,
  UserUpdateInput
} from './types'

/**
 * Preserve the canonical ApiError: the shared transport's interceptor already
 * maps AxiosErrors to it, so re-mapping through `toApiError` would degrade a
 * real 403 to the network fallback. Only strangers go through the mapper.
 */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

/** The active org id (or null pre-session) — every org-keyed fetch cache's key. */
const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

/** What an org-keyed list thunk returns: the rows plus the org they loaded for. */
interface OrgRows<T> {
  orgId: string | null
  rows: T
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchUsers = createAsyncThunk<OrgRows<ApiUser[]>, void, { rejectValue: ApiError }>(
  'users/fetchUsers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const rows = await usersHttp.usersHttp.list()
      return { orgId: orgKey(getState), rows }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { users: UsersState }).users.cache, orgKey(getState))
  }
)

export const fetchUser = createAsyncThunk<OrgRows<ApiUser>, string, { rejectValue: ApiError }>(
  'users/fetchUser',
  async (id, { getState, rejectWithValue }) => {
    try {
      const rows = await usersHttp.usersHttp.get(id)
      return { orgId: orgKey(getState), rows }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (id, { getState }) => {
      const { detail } = (getState() as { users: UsersState }).users
      return detail.id !== id || shouldFetch(detail.cache, orgKey(getState))
    }
  }
)

export const updateUser = createAsyncThunk<
  ApiUser,
  { id: string; body: UserUpdateInput },
  { rejectValue: ApiError }
>('users/updateUser', async ({ id, body }, { rejectWithValue }) => {
  try {
    return await usersHttp.usersHttp.update(id, body)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const inviteUser = createAsyncThunk<ApiInvite, InviteInput, { rejectValue: ApiError }>(
  'users/inviteUser',
  async (input, { rejectWithValue }) => {
    try {
      return await usersHttp.usersHttp.invite(input)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export const fetchInvites = createAsyncThunk<OrgRows<ApiInvite[]>, void, { rejectValue: ApiError }>(
  'users/fetchInvites',
  async (_, { getState, rejectWithValue }) => {
    try {
      const rows = await usersHttp.usersHttp.listInvites()
      return { orgId: orgKey(getState), rows }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { users: UsersState }).users.invites.cache, orgKey(getState))
  }
)

export const resendInvite = createAsyncThunk<ApiInvite, string, { rejectValue: ApiError }>(
  'users/resendInvite',
  async (inviteId, { rejectWithValue }) => {
    try {
      return await usersHttp.usersHttp.resendInvite(inviteId)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export const revokeInvite = createAsyncThunk<string, string, { rejectValue: ApiError }>(
  'users/revokeInvite',
  async (inviteId, { rejectWithValue }) => {
    try {
      await usersHttp.usersHttp.revokeInvite(inviteId)
      return inviteId
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

/**
 * A project-invite call's refusal, tagged with the address it was for: the
 * dialog maps a batch per address and a pending row shows its own refusal.
 */
export interface InviteRejection extends ApiError {
  email: string
}

const toInviteRejection = (error: unknown, email: string): InviteRejection => ({
  ...rejected(error),
  email
})

/** A project's pending invites. Keyed on the PROJECT: another project is another list. */
export const fetchProjectInvites = createAsyncThunk<ApiInvite[], string, { rejectValue: ApiError }>(
  'users/fetchProjectInvites',
  async (projectId, { rejectWithValue }) => {
    try {
      return await usersHttp.usersHttp.listProjectInvites(projectId)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (projectId, { getState }) => {
      const { projectInvites } = (getState() as { users: UsersState }).users
      return projectInvites.projectId !== projectId || shouldFetch(projectInvites.cache, projectId)
    }
  }
)

/** Invite ONE address to a project; the dialog fans a batch out one call at a time. */
export const inviteProjectMember = createAsyncThunk<
  ApiInvite,
  { projectId: string; input: ProjectInviteInput },
  { rejectValue: InviteRejection }
>('users/inviteProjectMember', async ({ projectId, input }, { rejectWithValue }) => {
  try {
    return await usersHttp.usersHttp.inviteToProject(projectId, input)
  } catch (error) {
    return rejectWithValue(toInviteRejection(error, input.email))
  }
})

/** Resend rotates the link; the answer is a new row, so the address (not the id) names the row. */
export const resendProjectInvite = createAsyncThunk<
  ApiInvite,
  { projectId: string; inviteId: string; email: string },
  { rejectValue: InviteRejection }
>('users/resendProjectInvite', async ({ projectId, inviteId, email }, { rejectWithValue }) => {
  try {
    return await usersHttp.usersHttp.resendProjectInvite(projectId, inviteId)
  } catch (error) {
    return rejectWithValue(toInviteRejection(error, email))
  }
})

export const revokeProjectInvite = createAsyncThunk<
  string,
  { projectId: string; inviteId: string; email: string },
  { rejectValue: InviteRejection }
>('users/revokeProjectInvite', async ({ projectId, inviteId, email }, { rejectWithValue }) => {
  try {
    await usersHttp.usersHttp.revokeProjectInvite(projectId, inviteId)
    return inviteId
  } catch (error) {
    return rejectWithValue(toInviteRejection(error, email))
  }
})

export const changeUserRole = createAsyncThunk<
  ApiUser,
  { userId: string; role: OrgRole },
  { rejectValue: ApiError }
>('users/changeUserRole', async ({ userId, role }, { rejectWithValue }) => {
  try {
    return await usersHttp.usersHttp.changeRole(userId, role)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const deactivateUser = createAsyncThunk<ApiUser, string, { rejectValue: ApiError }>(
  'users/deactivateUser',
  async (userId, { rejectWithValue }) => {
    try {
      return await usersHttp.usersHttp.deactivate(userId)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export const activateUser = createAsyncThunk<ApiUser, string, { rejectValue: ApiError }>(
  'users/activateUser',
  async (userId, { rejectWithValue }) => {
    try {
      return await usersHttp.usersHttp.activate(userId)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  }
)

export const fetchUserActivity = createAsyncThunk<
  ApiActivityEntry[],
  string,
  { rejectValue: ApiError }
>('users/fetchUserActivity', async (userId, { rejectWithValue }) => {
  try {
    return await usersHttp.usersHttp.activity(userId)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const fetchProjectMembers = createAsyncThunk<MemberRow[], string, { rejectValue: ApiError }>(
  'users/fetchProjectMembers',
  async (projectId, { rejectWithValue }) => {
    try {
      return await usersHttp.usersHttp.projectMembers(projectId)
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    // Keyed on the PROJECT (a different project is a different roster), with
    // the org guard riding the same cache: an org switch forces a reload too.
    condition: (projectId, { getState }) => {
      const { members } = (getState() as { users: UsersState }).users
      return members.projectId !== projectId || shouldFetch(members.cache, projectId)
    }
  }
)

export const fetchOrgProjects = createAsyncThunk<
  OrgRows<ApiProjectSummary[]>,
  void,
  { rejectValue: ApiError }
>(
  'users/fetchOrgProjects',
  async (_, { getState, rejectWithValue }) => {
    try {
      const rows = await usersHttp.usersHttp.orgProjects()
      return { orgId: orgKey(getState), rows }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { users: UsersState }).users.projects.cache, orgKey(getState))
  }
)

// ---------------------------------------------------------------------------
// State + slice
// ---------------------------------------------------------------------------

export interface UsersState {
  byId: Record<string, ApiUser>
  allIds: string[]
  cache: FetchCache
  error: ApiError | null
  invites: { items: ApiInvite[]; cache: FetchCache; error: ApiError | null }
  /**
   * One project's pending invites. `error` is the LIST-level read failure,
   * `blocked` a 403/404 on that read, `rowErrors` the refusals of one row's
   * resend/revoke keyed by the lower-cased address (a resend changes the id).
   */
  projectInvites: {
    projectId: string | null
    items: ApiInvite[]
    cache: FetchCache
    error: ApiError | null
    blocked: ApiError | null
    rowErrors: Record<string, ApiError>
  }
  detail: { id: string | null; cache: FetchCache; error: ApiError | null }
  activity: { byUserId: Record<string, ApiActivityEntry[]>; error: ApiError | null }
  members: {
    projectId: string | null
    items: MemberRow[]
    cache: FetchCache
    error: ApiError | null
  }
  projects: { items: ApiProjectSummary[]; cache: FetchCache; error: ApiError | null }
}

const initialState: UsersState = {
  byId: {},
  allIds: [],
  cache: initialFetchCache,
  error: null,
  invites: { items: [], cache: initialFetchCache, error: null },
  projectInvites: {
    projectId: null,
    items: [],
    cache: initialFetchCache,
    error: null,
    blocked: null,
    rowErrors: {}
  },
  detail: { id: null, cache: initialFetchCache, error: null },
  activity: { byUserId: {}, error: null },
  members: { projectId: null, items: [], cache: initialFetchCache, error: null },
  projects: { items: [], cache: initialFetchCache, error: null }
}

/** Merge one user row by id (create-on-unknown, overwrite-on-known). */
const mergeUser = (state: UsersState, user: ApiUser): void => {
  if (!(user.id in state.byId)) state.allIds.push(user.id)
  state.byId[user.id] = user
}

/** Upsert one invite row by id (an echo/resent row replaces its predecessor). */
const upsertInvite = (state: UsersState, invite: ApiInvite): void => {
  const existing = state.invites.items.findIndex((row) => row.id === invite.id)
  if (existing >= 0) state.invites.items[existing] = invite
  else state.invites.items.unshift(invite)
}

/**
 * A project-invite row refusal (resend or revoke) lands on that row, keyed by
 * address; a 409 means the row is no longer pending, so the list reloads and
 * it leaves. A refusal for another project's list is dropped.
 */
const projectInviteRowRefused = (
  state: UsersState,
  action: { payload?: InviteRejection; meta: { arg: { projectId: string; email: string } } }
): void => {
  const pi = state.projectInvites
  if (pi.projectId !== action.meta.arg.projectId || action.payload === undefined) return
  pi.rowErrors[action.meta.arg.email.toLowerCase()] = action.payload
  if (action.payload.status === 409) invalidate(pi.cache)
}

export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    /** Manual retry affordance: rearm the directory fetch. */
    directoryRetried(state) {
      invalidate(state.cache)
    },
    /** Manual retry affordance: rearm the invites fetch. */
    invitesRetried(state) {
      invalidate(state.invites.cache)
    },
    /** Rearm the project-invites load (Retry, and after a send); a no-op for another project. */
    projectInvitesRetried(state, action: PayloadAction<string>) {
      if (state.projectInvites.projectId === action.payload) invalidate(state.projectInvites.cache)
    },
    /** The pending list unmounted: the next open of the same project loads fresh. */
    projectInvitesClosed(state) {
      invalidate(state.projectInvites.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      // ── Directory ─────────────────────────────────────────────────────────
      .addCase(fetchUsers.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<OrgRows<ApiUser[]>>) => {
        fetchSucceeded(state.cache, action.payload.orgId)
        state.byId = {}
        state.allIds = []
        for (const user of action.payload.rows) mergeUser(state, user)
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      // ── Detail ────────────────────────────────────────────────────────────
      .addCase(fetchUser.pending, (state, action) => {
        fetchStarted(state.detail.cache)
        state.detail.id = action.meta.arg
        state.detail.error = null
      })
      .addCase(fetchUser.fulfilled, (state, action: PayloadAction<OrgRows<ApiUser>>) => {
        fetchSucceeded(state.detail.cache, action.payload.orgId)
        mergeUser(state, action.payload.rows)
      })
      .addCase(fetchUser.rejected, (state, action) => {
        fetchFailed(state.detail.cache)
        state.detail.error = action.payload ?? null
      })
      // ── Mutations (merge the response row; stale what they change) ───────
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<ApiUser>) => {
        mergeUser(state, action.payload)
        invalidate(state.cache)
      })
      .addCase(changeUserRole.fulfilled, (state, action: PayloadAction<ApiUser>) => {
        mergeUser(state, action.payload)
        invalidate(state.cache)
        invalidate(state.members.cache)
      })
      .addCase(deactivateUser.fulfilled, (state, action: PayloadAction<ApiUser>) => {
        mergeUser(state, action.payload)
        invalidate(state.cache)
      })
      .addCase(activateUser.fulfilled, (state, action: PayloadAction<ApiUser>) => {
        mergeUser(state, action.payload)
        invalidate(state.cache)
      })
      // ── Invites ───────────────────────────────────────────────────────────
      .addCase(fetchInvites.pending, (state) => {
        fetchStarted(state.invites.cache)
        state.invites.error = null
      })
      .addCase(fetchInvites.fulfilled, (state, action: PayloadAction<OrgRows<ApiInvite[]>>) => {
        fetchSucceeded(state.invites.cache, action.payload.orgId)
        state.invites.items = action.payload.rows
      })
      .addCase(fetchInvites.rejected, (state, action) => {
        fetchFailed(state.invites.cache)
        state.invites.error = action.payload ?? null
      })
      .addCase(inviteUser.fulfilled, (state, action: PayloadAction<ApiInvite>) => {
        upsertInvite(state, action.payload)
      })
      .addCase(resendInvite.fulfilled, (state, action: PayloadAction<ApiInvite>) => {
        upsertInvite(state, action.payload)
      })
      .addCase(revokeInvite.fulfilled, (state, action: PayloadAction<string>) => {
        state.invites.items = state.invites.items.filter((row) => row.id !== action.payload)
      })
      // ── Project invites ───────────────────────────────────────────────────
      .addCase(fetchProjectInvites.pending, (state, action) => {
        const pi = state.projectInvites
        fetchStarted(pi.cache, action.meta.arg)
        if (pi.projectId !== action.meta.arg) {
          pi.projectId = action.meta.arg
          pi.items = []
          pi.rowErrors = {}
        }
        pi.error = null
        pi.blocked = null
      })
      .addCase(fetchProjectInvites.fulfilled, (state, action) => {
        const pi = state.projectInvites
        if (!fetchSucceeded(pi.cache, action.meta.arg)) return
        pi.items = action.payload
        pi.rowErrors = {}
      })
      .addCase(fetchProjectInvites.rejected, (state, action) => {
        const pi = state.projectInvites
        if (!fetchFailed(pi.cache, action.meta.arg)) return
        const error = action.payload ?? null
        if (error !== null && (error.status === 403 || error.status === 404)) pi.blocked = error
        else pi.error = error
      })
      .addCase(inviteProjectMember.fulfilled, (state) => {
        invalidate(state.projectInvites.cache)
      })
      .addCase(resendProjectInvite.fulfilled, (state, action) => {
        const pi = state.projectInvites
        if (pi.projectId !== action.meta.arg.projectId) return
        const email = action.meta.arg.email.toLowerCase()
        delete pi.rowErrors[email]
        const index = pi.items.findIndex((row) => row.id === action.meta.arg.inviteId)
        if (index >= 0) pi.items[index] = action.payload
        else pi.items.unshift(action.payload)
      })
      .addCase(resendProjectInvite.rejected, projectInviteRowRefused)
      .addCase(revokeProjectInvite.fulfilled, (state, action) => {
        const pi = state.projectInvites
        if (pi.projectId !== action.meta.arg.projectId) return
        delete pi.rowErrors[action.meta.arg.email.toLowerCase()]
        pi.items = pi.items.filter((row) => row.id !== action.payload)
      })
      .addCase(revokeProjectInvite.rejected, projectInviteRowRefused)
      // ── Activity ──────────────────────────────────────────────────────────
      .addCase(fetchUserActivity.pending, (state) => {
        state.activity.error = null
      })
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        state.activity.byUserId[action.meta.arg] = action.payload
      })
      .addCase(fetchUserActivity.rejected, (state, action) => {
        state.activity.error = action.payload ?? null
      })
      // ── Members browser ───────────────────────────────────────────────────
      .addCase(fetchProjectMembers.pending, (state, action) => {
        fetchStarted(state.members.cache)
        state.members.projectId = action.meta.arg
        state.members.error = null
      })
      .addCase(
        fetchProjectMembers.fulfilled,
        (state, action: PayloadAction<MemberRow[], string, { arg: string }>) => {
          fetchSucceeded(state.members.cache, action.meta.arg)
          state.members.items = action.payload
        }
      )
      .addCase(fetchProjectMembers.rejected, (state, action) => {
        fetchFailed(state.members.cache)
        state.members.error = action.payload ?? null
      })
      // ── Project selector ──────────────────────────────────────────────────
      .addCase(fetchOrgProjects.pending, (state) => {
        fetchStarted(state.projects.cache)
        state.projects.error = null
      })
      .addCase(fetchOrgProjects.fulfilled, (state, action: PayloadAction<OrgRows<ApiProjectSummary[]>>) => {
        fetchSucceeded(state.projects.cache, action.payload.orgId)
        state.projects.items = action.payload.rows
      })
      .addCase(fetchOrgProjects.rejected, (state, action) => {
        fetchFailed(state.projects.cache)
        state.projects.error = action.payload ?? null
      })
  }
})

export const { directoryRetried, invitesRetried, projectInvitesRetried, projectInvitesClosed } =
  usersSlice.actions
